import React, { useEffect, useRef, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import BookConsultationProvider from "./components/BookConsultationProvider";
import TrackingInjector from "./components/TrackingInjector";
import { trackPageView } from "./lib/analytics";
import "./App.css";

// Route-level code splitting. Home stays in the main bundle (it's the most
// common landing page); every other route is fetched only when needed.
// This cuts ~50% of JS off the first-paint critical path.
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Work = lazy(() => import("./pages/Work"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const Thinking = lazy(() => import("./pages/Thinking"));
const ThinkingDetail = lazy(() => import("./pages/ThinkingDetail"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const ScheduleDiscussion = lazy(() => import("./pages/ScheduleDiscussion"));
const Strategy = lazy(() => import("./pages/Strategy"));
const Credentials = lazy(() => import("./pages/Credentials"));
const LinkedInPresence = lazy(() => import("./pages/LinkedInPresence"));
const LinkedInPresenceUSD = lazy(() => import("./pages/LinkedInPresenceUSD"));
const ProfilePower = lazy(() => import("./pages/ProfilePower"));
const AiSeo = lazy(() => import("./pages/AiSeo"));
const AiSeoCaseStudy = lazy(() => import("./pages/AiSeoCaseStudy"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Placeholder = lazy(() => import("./pages/Placeholder"));
const Admin = lazy(() => import("./pages/Admin"));
const ResetPasswordPage = lazy(() => import("./pages/admin/ResetPasswordPage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Fires a GA4 / GTM `page_view` event on every client-side route change.
// gtag.js fires its own pageview on initial HTML load (in /public/index.html),
// so we skip the very first render to avoid duplication.
function AnalyticsRouteTracker() {
  const { pathname, search } = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    trackPageView(pathname + (search || ""), document.title);
  }, [pathname, search]);
  return null;
}

function ChromeWrapper({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isDeck = pathname.startsWith("/credentials") || pathname.startsWith("/deck");
  const isProfilePower = pathname.startsWith("/profile-power");
  const isAiSeo = pathname.startsWith("/ai-seo");
  const chromeless = isAdmin || isDeck || isProfilePower || isAiSeo;
  return (
    <>
      {!chromeless && <Navigation />}
      {children}
      {!chromeless && <Footer />}
      {!chromeless && <BookConsultationProvider />}
    </>
  );
}

// Minimal Suspense fallback for lazy routes — a single neutral-colored
// full-viewport placeholder avoids layout shift while the route chunk loads
// and respects the dark vs light pages by staying close to the brand canvas.
function RouteSuspenseFallback() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAFAFA",
      }}
      aria-hidden="true"
    />
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AnalyticsRouteTracker />
        <TrackingInjector />
        <ChromeWrapper>
          <Suspense fallback={<RouteSuspenseFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/linkedin-presence" element={<LinkedInPresence />} />
              <Route path="/services/linkedin-presence-global" element={<LinkedInPresenceUSD />} />
              {/* Legacy redirect target — keep working until search engines re-index */}
              <Route path="/services/linkedin-presence-usd" element={<LinkedInPresenceUSD />} />
              <Route path="/profile-power" element={<ProfilePower />} />
              <Route path="/ai-seo" element={<AiSeo />} />
              <Route path="/ai-seo/case-studies/:slug" element={<AiSeoCaseStudy />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/work" element={<Work />} />
              <Route path="/work/:id" element={<CaseStudyDetail />} />
              <Route path="/thinking" element={<Thinking />} />
              <Route path="/our-thinking" element={<Thinking />} />
              <Route path="/thinking/:id" element={<ThinkingDetail />} />
              <Route path="/our-thinking/:id" element={<ThinkingDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/schedule-a-discussion" element={<ScheduleDiscussion />} />
              <Route path="/strategy" element={<Strategy />} />
              <Route path="/credentials" element={<Credentials />} />
              <Route path="/deck" element={<Credentials />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
              <Route path="/admin" element={<Admin />} />
              <Route
                path="*"
                element={
                  <Placeholder
                    title="Not found"
                    kicker="404"
                    blurb="The page you're looking for doesn't exist. Let's get you back home."
                  />
                }
              />
            </Routes>
          </Suspense>
        </ChromeWrapper>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
